import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, X, ArrowUpRight } from "lucide-react";

/**
 * CompareTray — sticky bottom pill that surfaces the current compare
 * list from localStorage. Renders nothing when the list is empty.
 * Cross-page: mounted at the layout level so it persists across
 * /store, /store/search, /store/product/:pid navigation.
 */

const KEY = "mirrorloops_compare_ids";
const MAX = 4;

export function getCompareIds() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

export function setCompareIds(ids) {
  localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
  window.dispatchEvent(new Event("mirrorloops:compare-change"));
}

export function useCompareIds() {
  const [ids, setIds] = useState(getCompareIds);
  useEffect(() => {
    const onChange = () => setIds(getCompareIds());
    window.addEventListener("mirrorloops:compare-change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("mirrorloops:compare-change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  return ids;
}

export function toggleCompareId(id) {
  const cur = getCompareIds();
  if (cur.includes(id)) {
    setCompareIds(cur.filter((x) => x !== id));
    return false;
  }
  if (cur.length >= MAX) return false;
  setCompareIds([...cur, id]);
  return true;
}

export default function CompareTray() {
  const ids = useCompareIds();
  const nav = useNavigate();
  if (!ids.length) return null;
  return (
    <div
      data-testid="compare-tray"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 border border-[#D4B878]/40 bg-[#141110]/95 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-3 shadow-2xl"
    >
      <Scale size={14} className="text-[#D4B878]" />
      <span className="text-xs text-white/80 font-mono">
        <span data-testid="compare-tray-count">{ids.length}</span> in compare
      </span>
      <div className="flex gap-1">
        {ids.map((id) => (
          <button
            key={id}
            data-testid={`compare-tray-chip-${id}`}
            onClick={() => {
              const cur = getCompareIds();
              setCompareIds(cur.filter((x) => x !== id));
            }}
            className="text-[10px] font-mono text-white/60 hover:text-white bg-white/5 border border-white/10 rounded-full px-2 py-0.5 flex items-center gap-1"
            title={id}
          >
            {id.slice(-5)} <X size={9} />
          </button>
        ))}
      </div>
      <button
        onClick={() => nav(`/store/compare?ids=${ids.join(",")}`)}
        disabled={ids.length < 2}
        data-testid="compare-tray-open"
        className="text-xs bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold rounded-full px-3 py-1 disabled:opacity-40 flex items-center gap-1"
      >
        Compare <ArrowUpRight size={11} />
      </button>
      <button
        onClick={() => setCompareIds([])}
        data-testid="compare-tray-clear"
        className="text-[10px] text-white/40 hover:text-white ml-1"
      >
        Clear
      </button>
    </div>
  );
}
